import React, { useState, useEffect, useRef } from "react";
import { BsFillPlayFill, BsCheck, BsChevronDown } from "react-icons/bs";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import * as localService from "../../services/localService";
import apiService from "../../services/apiService";
import logger from "../../utils/logger";
import { cn } from "../../lib/utils";
import "./styles.css";

interface BundleType {
  ordinal: number;
  value: "LOW" | "MEDIUM" | "HIGH";
  label: string;
}

interface Bundle {
  summary: string;
  id: number;
  bundleType: BundleType;
}

interface Package {
  id: number;
  cost: number;
  currency: string;
  billingPeriod: string;
  bundle: Bundle;
  createdOn: string | null;
  updatedOn: string;
}

interface FAQ {
  question: string;
  answer: string;
}

const CorporatePage = () => {
  const [view, setView] = useState("Enterprise");
  const [packages, setPackages] = useState<Package[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Reference to detect when component is mounted to apply navbar styles
  const pageRef = useRef<HTMLDivElement>(null);

  // Force transparent navbar immediately on component mount
  useEffect(() => {
    // Add transparent-navbar-force class to the body
    document.body.classList.add("transparent-navbar-force");

    // Find all possible navbar elements
    const header = document.querySelector("header");

    if (header) {
      // Force transparency directly on the header
      header.style.backgroundColor = "transparent";
      header.style.backdropFilter = "none";
      header.style.border = "none";
      header.style.boxShadow = "none";

      // Make all children transparent too - super aggressive approach
      const applyTransparentStyles = (element: HTMLElement) => {
        element.style.backgroundColor = "transparent";
        element.style.backdropFilter = "none";
        element.style.background = "none";
        element.style.boxShadow = "none";
        element.style.border = "none";

        // Apply to children recursively
        Array.from(element.children).forEach(child => {
          if (child instanceof HTMLElement) {
            applyTransparentStyles(child);
          }
        });
      };

      // Apply to header and all its children recursively
      applyTransparentStyles(header);

      // Make text black on all text elements
      const textElements = document.querySelectorAll(
        "header a, header button, header span, nav a, nav button, nav span"
      );
      textElements.forEach(element => {
        if (element instanceof HTMLElement) {
          element.style.color = "black";
          element.style.textShadow = "none";
        }
      });
    }

    // This effect only runs once on mount
  }, []);

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrolled]);

  // Apply transparent navbar effect with scroll behavior
  useEffect(() => {
    // Add transparent-navbar-force class to the body when not scrolled
    if (!scrolled) {
      document.body.classList.add("transparent-navbar-force");
    } else {
      document.body.classList.remove("transparent-navbar-force");
    }

    // Find all possible navbar elements and containers
    const header = document.querySelector("header");
    const navs = document.querySelectorAll("nav");
    const navbarContainers = document.querySelectorAll("header > div, header > nav > div, .navbar-container");

    if (header) {
      // Store original styles
      const originalBg = header.style.backgroundColor;
      const originalBorder = header.style.borderBottom;
      const originalFilter = header.style.backdropFilter;

      // Function to update styles based on scroll position
      const updateStyles = () => {
        if (scrolled) {
          // Remove the force class when scrolled
          document.body.classList.remove("transparent-navbar-force");

          // Scrolled style - white background with shadow
          header.style.backgroundColor = "rgba(255, 255, 255, 0.9)";
          header.style.backdropFilter = "blur(8px)";
          header.style.borderBottom = "1px solid rgba(0, 0, 0, 0.1)";
          header.style.boxShadow = "0 2px 10px rgba(0, 0, 0, 0.1)";

          // Text should always be black
          const links = header.querySelectorAll("a, button, span");
          links.forEach(link => {
            if (link instanceof HTMLElement) {
              link.style.color = "black";
            }
          });

          // Style nav elements
          navs.forEach(nav => {
            nav.style.backgroundColor = "transparent";
            nav.style.borderBottom = "none";
          });

          // Style any navbar containers
          navbarContainers.forEach(container => {
            if (container instanceof HTMLElement) {
              container.style.backgroundColor = "transparent";
            }
          });
        } else {
          // Add the force class when at top
          document.body.classList.add("transparent-navbar-force");

          // Top position style - fully transparent
          header.style.backgroundColor = "transparent";
          header.style.backdropFilter = "none";
          header.style.borderBottom = "none";
          header.style.boxShadow = "none";

          // Text should always be black
          const links = header.querySelectorAll("a, button, span");
          links.forEach(link => {
            if (link instanceof HTMLElement) {
              link.style.color = "black";
            }
          });

          // Style nav elements
          navs.forEach(nav => {
            nav.style.backgroundColor = "transparent";
            nav.style.borderBottom = "none";
          });

          // Style any navbar containers to be fully transparent
          navbarContainers.forEach(container => {
            if (container instanceof HTMLElement) {
              container.style.backgroundColor = "transparent";
              container.style.backdropFilter = "none";
            }
          });
        }
      };

      // Initial update
      updateStyles();

      // Cleanup function
      return () => {
        // Remove the class when unmounting
        document.body.classList.remove("transparent-navbar-force");

        // Restore original styles when unmounting
        if (header) {
          header.style.backgroundColor = originalBg;
          header.style.backdropFilter = originalFilter;
          header.style.borderBottom = originalBorder;
          header.style.boxShadow = "";

          const links = header.querySelectorAll("a, button");
          links.forEach(link => {
            if (link instanceof HTMLElement) {
              link.style.color = "";
            }
          });

          navs.forEach(nav => {
            nav.style.backgroundColor = "";
            nav.style.borderBottom = "";
          });

          navbarContainers.forEach(container => {
            if (container instanceof HTMLElement) {
              container.style.backgroundColor = "";
              container.style.backdropFilter = "";
            }
          });
        }
      };
    }
  }, [scrolled]);

  // Fetch packages on component mount
  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const currentUser = localService.getUser();
      const userId = currentUser ? currentUser.id : 0;
      const response = await apiService.getBundlePricings(userId);

      if (response.data && Array.isArray(response.data.data)) {
        setPackages(response.data.data);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      logger.error("Error fetching packages:", error);
      setError("Failed to fetch packages");
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to get package price by bundle type value
  const getPackagePrice = (type: "LOW" | "MEDIUM" | "HIGH"): number | null => {
    const pkg = packages.find(p => p.bundle.bundleType.value === type);
    return pkg ? pkg.cost : null;
  };

  // Helper function to get package currency by bundle type value
  const getPackageCurrency = (type: "LOW" | "MEDIUM" | "HIGH"): string => {
    const pkg = packages.find(p => p.bundle.bundleType.value === type);
    return pkg ? pkg.currency : "KES";
  };

  // Helper function to get package summary by bundle type value
  const getPackageSummary = (type: "LOW" | "MEDIUM" | "HIGH"): string => {
    const pkg = packages.find(p => p.bundle.bundleType.value === type);
    return pkg?.bundle.summary || "";
  };

  // Get both prices and summaries
  const enterprisePrice = getPackagePrice("LOW") || 29000;
  const corporatePrice = getPackagePrice("MEDIUM") || 25000;
  const corporatePlusPrice = getPackagePrice("HIGH") || 23000;

  const enterpriseCurrency = getPackageCurrency("LOW");
  const corporateCurrency = getPackageCurrency("MEDIUM");
  const corporatePlusCurrency = getPackageCurrency("HIGH");

  const enterpriseSummary = getPackageSummary("LOW") || "Tailored for teams of 5-10.";
  const corporateSummary = getPackageSummary("MEDIUM") || "Tailored for teams of 11-19.";
  const corporatePlusSummary = getPackageSummary("HIGH") || "Tailored for teams of 20 and above.";

  const handleCloseVideoModal = () => {
    setShowVideoModal(false);
  };

  return (
    <div className="w-full" ref={pageRef}>
      {/* Hero Section - Full viewport width and starts at the very top */}
      <div className="relative w-full bg-gradient-to-b from-blue-500 to-transparent corporate-page-hero">
        <div className="text-center">
          <h1 className="text-2xl md:text-4xl font-semibold text-white font-montserrat leading-tight">INTRODUCING</h1>
          <h1 className="text-2xl md:text-4xl font-semibold text-white font-montserrat leading-tight">
            SPACIA ENTERPRISE
          </h1>
        </div>

        <div className="w-full flex justify-center mt-8 md:mt-12">
          <button
            onClick={() => setShowVideoModal(true)}
            className="flex items-center px-6 py-3 md:px-8 md:py-4 rounded-full bg-black/20 cursor-pointer text-white shadow-lg"
          >
            <BsFillPlayFill className="bg-white/10 text-white p-1 rounded-full text-2xl md:text-3xl mr-2" />
            <p>Watch Demo</p>
          </button>
        </div>

        <div className="flex justify-center my-8 md:my-12">
          <img
            src="/assets/img/backgrounds/men-with-laptops-only.svg"
            alt="men with laptops"
            className="w-full max-w-4xl px-4"
          />
        </div>

        <img
          src="/assets/img/enterprise/telegram-kite.svg"
          alt="telegram kite"
          className="absolute bottom-32 left-0 w-16 md:w-auto hidden md:block"
        />
        <img
          src="/assets/img/enterprise/dollar-kite.svg"
          alt="dollar kite"
          className="absolute bottom-40 right-0 w-16 md:w-auto hidden md:block"
        />
      </div>
      {/* Spacia plans */}
      <div className="px-4 md:px-10 pt-10">
        <div className="relative h-64 md:h-96 p-4 md:p-10 bg-cover bg-center enterprise_your_business rounded-t-lg">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center font-poppins">
            <h6 className="bg-white rounded-full px-4 py-1 md:py-2 font-medium text-sm md:text-base mb-4 md:mb-0">
              Plans Tailored for your business
            </h6>
            <div className="flex flex-wrap gap-2">
              <button
                className={cn(
                  "rounded-full px-4 py-1 md:py-2 text-sm md:text-base transition-colors",
                  view === "Enterprise" ? "bg-[#00B6F0] text-white" : "bg-white"
                )}
                onClick={() => setView("Enterprise")}
              >
                Enterprise
              </button>
              <button
                className={cn(
                  "rounded-full px-4 py-1 md:py-2 text-sm md:text-base transition-colors",
                  view === "Corporate" ? "bg-[#00B6F0] text-white" : "bg-white"
                )}
                onClick={() => setView("Corporate")}
              >
                Corporate
              </button>
              <button
                className={cn(
                  "rounded-full px-4 py-1 md:py-2 text-sm md:text-base transition-colors",
                  view === "Corporate Plus" ? "bg-[#00B6F0] text-white" : "bg-white"
                )}
                onClick={() => setView("Corporate Plus")}
              >
                Corporate Plus
              </button>
            </div>
          </div>
          <div className="flex flex-col items-end text-white mt-10 md:mt-32 font-montserrat">
            <h1 className="text-xl md:text-3xl lg:text-4xl font-bold">Multiple work locations</h1>
            <h1 className="text-xl md:text-3xl lg:text-4xl font-bold">One membership</h1>
          </div>
        </div>

        <div className="bg-black text-white py-8 md:py-12 px-4 md:px-12 rounded-b-lg">
          {/* Enterprise */}
          {view === "Enterprise" && (
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
              <div className="flex flex-col">
                <h1 className="text-3xl md:text-5xl font-black my-4 font-montserrat">Enterprise</h1>
                <h6 className="text-sm md:text-base">{enterpriseSummary}</h6>
                <h1 className="text-3xl md:text-5xl font-black my-4 font-montserrat">
                  {enterpriseCurrency} {enterprisePrice}
                  <span className="text-sm md:text-base font-medium">/month per user</span>
                </h1>
                <h3 className="text-[#00B6F0] mb-4 text-xl md:text-2xl">Up to 14% savings</h3>
                <button
                  className="bg-[#00B6F0] text-white rounded-full py-3 px-8 md:px-12 text-base"
                  onClick={() => window.location.assign("/package-signup")}
                >
                  Get Started
                </button>
              </div>
              <div className="space-y-3 md:space-y-4">
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">
                    Unlimited <span className="text-[#00B6F0]">Dedicated Co-Working Space</span>
                  </p>
                </div>
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">10 Hour Meeting Room Access</p>
                </div>
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">High speed Wi-Fi</p>
                </div>
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">Food & Beverage Discounts</p>
                </div>
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">Networking Events</p>
                </div>
              </div>
            </div>
          )}

          {/* Corporate */}
          {view === "Corporate" && (
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
              <div className="flex flex-col">
                <h1 className="text-3xl md:text-5xl font-black my-4 font-montserrat">Corporate</h1>
                <h6 className="text-sm md:text-base">{corporateSummary}</h6>
                <h1 className="text-3xl md:text-5xl font-black my-4 font-montserrat">
                  {corporateCurrency} {corporatePrice}
                  <span className="text-sm md:text-base font-medium">/month per user</span>
                </h1>
                <h3 className="text-[#00B6F0] mb-4 text-xl md:text-2xl">Up to 21% savings</h3>
                <button
                  className="bg-[#00B6F0] text-white rounded-full py-3 px-8 md:px-12 text-base"
                  onClick={() => window.location.assign("/package-signup")}
                >
                  Get Started
                </button>
              </div>
              <div className="space-y-3 md:space-y-4">
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">
                    Unlimited <span className="text-[#00B6F0]">Dedicated Co-Working Space</span>
                  </p>
                </div>
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">15 Hour Meeting Room Access</p>
                </div>
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">Food & Beverage Discounts</p>
                </div>
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">Dedicated High speed Wi-Fi</p>
                </div>
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">Networking Events</p>
                </div>
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">Business Address</p>
                </div>
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">Mail Handling Services</p>
                </div>
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">Free Printing & Scanning</p>
                </div>
              </div>
            </div>
          )}

          {/* Corporate Plus */}
          {view === "Corporate Plus" && (
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
              <div className="flex flex-col">
                <h1 className="text-3xl md:text-5xl font-black my-4 font-montserrat">Corporate Plus</h1>
                <h6 className="text-sm md:text-base">{corporatePlusSummary}</h6>
                <h1 className="text-3xl md:text-5xl font-black my-4 font-montserrat">
                  {corporatePlusCurrency} {corporatePlusPrice}
                  <span className="text-sm md:text-base font-medium">/month per user</span>
                </h1>
                <h3 className="text-[#00B6F0] mb-4 text-xl md:text-2xl">Up to 27% savings</h3>
                <button
                  className="bg-[#00B6F0] text-white rounded-full py-3 px-8 md:px-12 text-base"
                  onClick={() => window.location.assign("/package-signup")}
                >
                  Get Started
                </button>
              </div>
              <div className="space-y-3 md:space-y-4">
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">
                    Unlimited <span className="text-[#00B6F0]">Dedicated Co-Working Space</span>
                  </p>
                </div>
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">Private Office Access</p>
                </div>
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">20 Hour Meeting Room Access</p>
                </div>
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">Food & Beverage Discounts</p>
                </div>
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">Dedicated High speed Wi-Fi</p>
                </div>
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">Networking Events</p>
                </div>
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">Business Address</p>
                </div>
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">Mail Handling Services</p>
                </div>
                <div className="flex items-center">
                  <BsCheck className="bg-[#00B6F0] p-1 text-lg md:text-2xl rounded-full mr-2" />
                  <p className="text-sm md:text-base">Free Printing & Scanning</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Sign up today */}
      <div className="flex flex-col items-center px-4 md:px-20 py-10 md:py-16 text-center">
        <h2 className="text-2xl md:text-3xl lg:text-4xl mb-6 font-medium text-[#011A49] font-montserrat">
          Sign up today and find a <span className="text-[#3078FF]">space</span> to call home
        </h2>
        <p className="text-sm md:text-base text-[#011A49]">
          Buy a pass to work from hotels, cafes or co-working spaces OR book private meeting rooms,
        </p>
        <p className="text-sm md:text-base text-[#011A49]">offices or creative spaces across our cities.</p>
        <div className="mt-8 md:mt-10">
          <button
            className="bg-[#3078FF] text-white rounded-full py-3 px-6 md:px-8 text-sm md:text-base font-medium shadow-lg"
            onClick={() => window.location.assign("/package-signup")}
          >
            Get Started
          </button>
        </div>
      </div>
      {/* FAQ */}
      <section id="faqSection" className="flex flex-col items-center px-4 md:px-20 py-10 md:py-20">
        <p className="font-medium text-[#3078FF] text-sm md:text-base">HAVE ANYTHING TO ASK</p>
        <h1 className="text-2xl md:text-3xl lg:text-4xl text-[#011A49] mb-8 md:mb-10 font-montserrat">
          Frequently asked questions
        </h1>

        {/* SPACIA HOST FAQs */}
        <div className="w-full mb-8 md:mb-10">
          <h2 className="text-xl md:text-2xl text-[#011A49] mb-4 md:mb-6 font-medium">Spacia Host FAQs</h2>

          <div className="w-full mb-3 md:mb-4">
            <Accordion>
              <AccordionSummary expandIcon={<BsChevronDown />}>
                <p className="text-sm md:text-base">How do I get the payment?</p>
              </AccordionSummary>
              <AccordionDetails>
                <p className="text-sm md:text-base text-gray-600">
                  All payouts will be made within 48hrs of guest check-in unless otherwise agreed.
                </p>
              </AccordionDetails>
            </Accordion>
          </div>

          <div className="w-full mb-3 md:mb-4">
            <Accordion>
              <AccordionSummary expandIcon={<BsChevronDown />}>
                <p className="text-sm md:text-base">What is the cancellation policy?</p>
              </AccordionSummary>
              <AccordionDetails>
                <p className="text-sm md:text-base text-gray-600">
                  In general, if a client cancels a reservation, the amount paid to Host is determined by the
                  cancellation policy that applies to that reservation.
                </p>
              </AccordionDetails>
            </Accordion>
          </div>

          <div className="w-full mb-3 md:mb-4">
            <Accordion>
              <AccordionSummary expandIcon={<BsChevronDown />}>
                <p className="text-sm md:text-base">
                  How often will the availability schedules be updated? Can we do the updates ourselves?
                </p>
              </AccordionSummary>
              <AccordionDetails>
                <p className="text-sm md:text-base text-gray-600">
                  The host is in control of listing and can set prices, availability, and terms of use for their space.
                </p>
              </AccordionDetails>
            </Accordion>
          </div>

          <div className="w-full mb-3 md:mb-4">
            <Accordion>
              <AccordionSummary expandIcon={<BsChevronDown />}>
                <p className="text-sm md:text-base">What is the cost of listing on Spacia?</p>
              </AccordionSummary>
              <AccordionDetails>
                <p className="text-sm md:text-base text-gray-600">
                  Listing on Spacia is free for the first year of use. Spacia retains a 15% commission on the amount
                  paid for reservation. (variable commission structure, check with..)
                </p>
              </AccordionDetails>
            </Accordion>
          </div>
        </div>

        {/* SPACIA USERS FAQs */}
        <div className="w-full mb-8 md:mb-10">
          <h2 className="text-xl md:text-2xl text-[#011A49] mb-4 md:mb-6 font-medium">Spacia Users FAQs</h2>

          <div className="w-full mb-3 md:mb-4">
            <Accordion>
              <AccordionSummary expandIcon={<BsChevronDown />}>
                <p className="text-sm md:text-base">Do I have to pay to use the site?</p>
              </AccordionSummary>
              <AccordionDetails>
                <p className="text-sm md:text-base text-gray-600">
                  Spacia Africa has different categories and packages catered to enterprise users as well as all who
                  would want to have access to Spacia spaces, with plans based on usage from basic, to plus to premium.
                </p>
              </AccordionDetails>
            </Accordion>
          </div>

          <div className="w-full mb-3 md:mb-4">
            <Accordion>
              <AccordionSummary expandIcon={<BsChevronDown />}>
                <p className="text-sm md:text-base">
                  Can I use Spacia as an individual away from my organisation's spaces?
                </p>
              </AccordionSummary>
              <AccordionDetails>
                <p className="text-sm md:text-base text-gray-600">
                  Spacia's message of work anywhere anytime is fundamental to our business model, and as such we cater
                  for individual work wherever they feel comfortable across all our spaces.
                </p>
              </AccordionDetails>
            </Accordion>
          </div>

          <div className="w-full mb-3 md:mb-4">
            <Accordion>
              <AccordionSummary expandIcon={<BsChevronDown />}>
                <p className="text-sm md:text-base">
                  Am I limited in terms of how far I can work or how many times I can access a space?
                </p>
              </AccordionSummary>
              <AccordionDetails>
                <p className="text-sm md:text-base text-gray-600">
                  Spacia provides access to all our reserved as well as all spaces that our hosts have made open or
                  available, and any amenity on the platform can be utilised.
                </p>
              </AccordionDetails>
            </Accordion>
          </div>

          <div className="w-full mb-3 md:mb-4">
            <Accordion>
              <AccordionSummary expandIcon={<BsChevronDown />}>
                <p className="text-sm md:text-base">Will I have access to Spacia events and all such promotions?</p>
              </AccordionSummary>
              <AccordionDetails>
                <p className="text-sm md:text-base text-gray-600">
                  Spacia events are accessible to all members of our platform, enterprise or otherwise, and the only
                  requirement to fulfil is be under the Spacia package that caters to the aforementioned.
                </p>
              </AccordionDetails>
            </Accordion>
          </div>

          <div className="w-full mb-3 md:mb-4">
            <Accordion>
              <AccordionSummary expandIcon={<BsChevronDown />}>
                <p className="text-sm md:text-base">Can I upgrade or downgrade my membership package?</p>
              </AccordionSummary>
              <AccordionDetails>
                <p className="text-sm md:text-base text-gray-600">
                  Spacia offers the flexibility to not only improve your membership situation but also to switch to a
                  package you feel comfortable spending on, depending on Spacia's terms and conditions of service with
                  regards to cancellation.
                </p>
              </AccordionDetails>
            </Accordion>
          </div>

          <div className="w-full mb-3 md:mb-4">
            <Accordion>
              <AccordionSummary expandIcon={<BsChevronDown />}>
                <p className="text-sm md:text-base">How is my data used by Spacia Africa?</p>
              </AccordionSummary>
              <AccordionDetails>
                <p className="text-sm md:text-base text-gray-600">
                  Spacia takes usage of your data very seriously, and the collected information has the benefit of
                  helping Spacia know where and when the majority of our efforts towards producing a better product
                  should be focused.
                </p>
              </AccordionDetails>
            </Accordion>
          </div>
        </div>

        {/* SPACIA PRICING PACKAGES FAQs */}
        <div className="w-full">
          <h2 className="text-xl md:text-2xl text-[#011A49] mb-4 md:mb-6 font-medium">Spacia Pricing Packages FAQs</h2>

          <div className="w-full mb-3 md:mb-4">
            <Accordion>
              <AccordionSummary expandIcon={<BsChevronDown />}>
                <p className="text-sm md:text-base">What are the different pricing packages offered to clients?</p>
              </AccordionSummary>
              <AccordionDetails>
                <p className="text-sm md:text-base text-gray-600">
                  The three pricing packages offered are Enterprise, Corporate, and Corporate Plus. Each package has
                  different levels of offerings as well as different quantities of people it can serve.
                </p>
              </AccordionDetails>
            </Accordion>
          </div>

          <div className="w-full mb-3 md:mb-4">
            <Accordion>
              <AccordionSummary expandIcon={<BsChevronDown />}>
                <p className="text-sm md:text-base">Can clients book multiple locations in a month?</p>
              </AccordionSummary>
              <AccordionDetails>
                <p className="text-sm md:text-base text-gray-600">
                  For the Enterprise package, clients are expected to pick a primary location and work from it for the
                  duration of the month. However, for Corporate and Corporate Plus, these packages have larger numbers
                  of users who may need more than one location to work at during the month. Corporate allows for 1
                  primary location and 1 secondary location, while Corporate Plus allows for 1 primary location and 3
                  secondary locations, with an extra secondary location if the number of users exceeds 20.
                </p>
              </AccordionDetails>
            </Accordion>
          </div>

          <div className="w-full mb-3 md:mb-4">
            <Accordion>
              <AccordionSummary expandIcon={<BsChevronDown />}>
                <p className="text-sm md:text-base">What is the definition of a primary location?</p>
              </AccordionSummary>
              <AccordionDetails>
                <p className="text-sm md:text-base text-gray-600">
                  A primary location is where the user/client envisions themselves working, for at least 60% of the
                  working days and is where they would be for the majority of that month.
                </p>
              </AccordionDetails>
            </Accordion>
          </div>

          <div className="w-full mb-3 md:mb-4">
            <Accordion>
              <AccordionSummary expandIcon={<BsChevronDown />}>
                <p className="text-sm md:text-base">Can I upgrade to a higher package once my team size increases?</p>
              </AccordionSummary>
              <AccordionDetails>
                <p className="text-sm md:text-base text-gray-600">
                  Yes, movement between packages will be seamless to accommodate the clients growing needs for space, as
                  well as adjust to smaller team sizes when necessary by the provision of multiple packages.
                </p>
              </AccordionDetails>
            </Accordion>
          </div>
        </div>
      </section>
      {/* Video Modal */}
      {showVideoModal && (
        <div
          className="fixed inset-0 bg-black/70 flex justify-center items-center z-50"
          onClick={handleCloseVideoModal}
        >
          <div className="relative w-4/5 max-w-3xl aspect-video bg-black" onClick={e => e.stopPropagation()}>
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/PR4YWa3SRQ4?autoplay=1"
              title="Spacia Enterprise Demo"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
            <button
              onClick={handleCloseVideoModal}
              className="absolute -top-10 right-0 bg-transparent border-none text-white text-2xl cursor-pointer"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorporatePage;
